import React, { useEffect, useState } from "react";
import { BrandLogo } from "@/components/shared";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, ListChecks, Wallet, Disc3, Users,
  BadgeCheck, User, Settings as Cog, Bell, LifeBuoy, LogOut, Trophy, Menu, X,
  ArrowDownToLine, ArrowUpFromLine, ShieldCheck, Plane, CupSoda, Package,
} from "lucide-react";
import LangToggle from "@/components/LangToggle";
import LiveClock from "@/components/LiveClock";
import AdsGate from "@/components/AdsGate";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { fmtPKR } from "@/lib/format";
import { api } from "@/lib/api";

const items = [
  ["/dashboard", "dashboard", LayoutDashboard],
  ["/events", "events", CalendarDays],
  ["/my-predictions", "my_predictions", ListChecks],
  ["/balance", "balance", Wallet],
  ["/deposit", "deposit", ArrowDownToLine],
  ["/withdrawal", "withdrawal", ArrowUpFromLine],
  ["/protection", "protection", ShieldCheck],
  ["/spin", "spin", Disc3],
  ["/aviator", "aviator", Plane],
  ["/three-cups", "three_cups", CupSoda],
  ["/deal-or-no-deal", "dnd", Package],
  ["/referral", "referral", Users],
  ["/kyc", "kyc", BadgeCheck],
  ["/profile", "profile", User],
  ["/settings", "settings", Cog],
  ["/notifications", "notifications", Bell],
  ["/support", "support", LifeBuoy],
];

export default function MemberLayout() {
  const { t } = useI18n();
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const load = () => {
      api.get("/notifications").then(({ data }) => setUnread(data.filter((n) => !n.read).length)).catch(() => {});
      api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => {});
    };
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [location.pathname, setUser]);

  return (
    <div className="ec-bg min-h-screen flex">
      {/* SIDEBAR */}
      <aside className={`fixed z-40 inset-y-0 start-0 w-64 glass border-e border-white/10 flex flex-col transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"}`}>
        <div className="h-16 flex items-center gap-2 px-5 font-head font-extrabold border-b border-white/10">
          <BrandLogo size="w-10 h-10" text={t("brand")} textClass="text-base" testid="member-brand-logo" />
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map(([to, k, Icon]) => (
            <NavLink key={to} to={to} data-testid={`nav-${k}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-orange-500/15 text-orange-400 border border-orange-500/30" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>
              <Icon className="w-5 h-5" /> {t(k)}
              {k === "notifications" && unread > 0 && <span className="ms-auto bg-orange-500 text-pure text-xs rounded-full px-2">{unread}</span>}
            </NavLink>
          ))}
          <button onClick={logout} data-testid="logout-button" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 mt-4">
            <LogOut className="w-5 h-5" /> {t("logout")}
          </button>
        </nav>
      </aside>
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* MAIN */}
      <div className="flex-1 lg:ms-64 min-w-0">
        <header className="sticky top-0 z-20 glass h-16 flex items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="member-menu-toggle">{open ? <X /> : <Menu />}</button>
            <LiveClock className="hidden sm:inline-flex" />
          </div>
          <div className="flex items-center gap-3">
            <div className="text-end">
              <p className="text-xs text-gray-500 leading-none">{t("current_balance")}</p>
              <p className="font-head font-bold text-gradient" data-testid="header-balance">{fmtPKR(user?.balance)}</p>
            </div>
            <LangToggle compact />
            <div className="w-9 h-9 rounded-full bg-orange-500/20 flex items-center justify-center font-bold text-orange-400 uppercase">{user?.name?.[0] || "U"}</div>
          </div>
        </header>
        <main className="p-5 sm:p-8 max-w-6xl mx-auto"><AdsGate><Outlet /></AdsGate></main>
      </div>
    </div>
  );
}
